import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";

type TagsInputModel = { id: string; text: string };

export function TimeSlotsAutocomplete({
    options,
    value,
    onAdd,
    onDelete,
    onChipClick,
    placeholder,
}: {
    options: TagsInputModel[];
    value: TagsInputModel[];
    onAdd: (tag: TagsInputModel) => void;
    onDelete: (index: number) => void;
    onChipClick?: (index: number) => void;
    placeholder?: string;
}) {
    const baseFilter = createFilterOptions<TagsInputModel>();

    // convert union values coming from freeSolo into your object shape
    const normalize = (arr: Array<TagsInputModel | string>): TagsInputModel[] =>
        arr.map((v) => (typeof v === "string" ? { id: v, text: v } : v));

    return (
        <Autocomplete<TagsInputModel, true, false, true>
            multiple
            freeSolo
            fullWidth
            size="small"
            options={options}          // [{id,text}] suggestions
            value={value}              // [{id,text}] selected
            openOnFocus
            filterOptions={(opts, params) => {
                const filtered = baseFilter(opts, params); // TagsInputModel[]
                const { inputValue } = params;

                const exists =
                    opts.some((o) => o.text.toLowerCase() === inputValue.toLowerCase()) ||
                    value.some((o) => o.text.toLowerCase() === inputValue.toLowerCase());

                if (inputValue !== "" && !exists) {
                    filtered.push({ id: inputValue, text: inputValue }); // create as object
                }
                return filtered; // TagsInputModel[]
            }}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.text)}
            isOptionEqualToValue={(opt, val) =>
                (typeof opt === "string" ? opt : opt.id) ===
                (typeof val === "string" ? val : val.id)
            }
            onChange={(_, newVal) => {
                const next = normalize(newVal as Array<TagsInputModel | string>);

                // additions (present in next but not in value)
                next.forEach((n) => {
                    const found = value.some((c) => c.id === n.id);
                    if (!found) onAdd(n);
                });

                // deletions (present in value but not in next) — call by index
                value.forEach((c, idx) => {
                    const stillThere = next.some((n) => n.id === c.id);
                    if (!stillThere) onDelete(idx);
                });
            }}
            renderTags={(selected, getTagProps) =>
                (selected as TagsInputModel[]).map((tag, index) => {
                    const { onDelete: onChipDelete, ...rest } = getTagProps({ index });
                    return (
                        <Chip
                            {...rest}
                            onDelete={onChipDelete}
                            key={`${tag.id}-${index}`}
                            variant="outlined"
                            label={tag.text}
                            onClick={onChipClick ? () => onChipClick(index) : undefined}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    multiline
                    rows={2}
                    {...params}
                    placeholder={placeholder ?? "Add a time slot"}
                    required
                />
            )}
        />
    );
}
